/* Recon-only enhancement. Native Shopify form submission remains authoritative.
   Never changes the 12-pack selector, global flavor, prices, or existing images. */
(() => {
  'use strict';
  if (customElements.get('aa-recon-pack')) return;

  class AAReconPack extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      // When an already-defined element is streamed into the DOM, wait for
      // its form children before enhancing it.
      if (!this.querySelector('[data-recon-caption]')) {
        clearTimeout(this._childrenTimer);
        this._childrenTimer = setTimeout(() => {
          if (this.isConnected) this.connectedCallback();
        }, 0);
        return;
      }
      this._connected = true;
      this._abort = new AbortController();
      const events = { signal: this._abort.signal };
      this.select = this.querySelector('[data-recon-select]');
      this.quantity = this.querySelector('[data-recon-quantity]');
      this.form = this.querySelector('[data-recon-form]');
      this.buy = this.querySelector('[data-recon-submit]');
      this.price = this.querySelector('[data-recon-price]');
      this.status = this.querySelector('[data-recon-status]');
      this.minus = this.querySelector('[data-recon-minus]');
      this.plus = this.querySelector('[data-recon-plus]');
      this.buttons = [...this.querySelectorAll('[data-recon-flavor]')];
      if (!this.select || !this.quantity || !this.form || !this.buy) return;
      this.options = [...this.select.options].filter(option => option.dataset.flavor);
      this.selected = this.options.find(option => option.dataset.flavor === this.dataset.flavor)
        || this.options.find(option => !option.disabled) || this.options[0];
      this.buttons.forEach(button => {
        const option = this.options.find(item => item.dataset.flavor === button.dataset.reconFlavor);
        button.disabled = !option || option.disabled;
        if (option && option.dataset.state === 'soldout') button.textContent += ' · Sold out';
        else if (!option || option.disabled) button.textContent += ' · Unavailable';
        button.addEventListener('click', () => {
          if (button.disabled || !option || option.disabled) return;
          this.selected = option;
          this.sync(true);
        }, events);
      });
      this.select.addEventListener('change', () => {
        const option = this.select.selectedOptions[0];
        if (option && option.dataset.flavor && !option.disabled) {
          this.selected = option;
          this.sync(true);
        }
      }, events);
      this.minus.addEventListener('click', () => this.adjust(-1), events);
      this.plus.addEventListener('click', () => this.adjust(1), events);
      this.quantity.addEventListener('input', () => this.syncQuantityButtons(), events);
      this.quantity.addEventListener('change', () => {
        this.normalizeQuantity();
        this.syncQuantityButtons();
      }, events);
      this.form.addEventListener('submit', event => {
        if (!this.selected || this.selected.disabled || !this.select.value) {
          event.preventDefault();
          this.status.textContent = 'Please choose an available Recon Pack.';
          return;
        }
        this.normalizeQuantity();
        if (!this.form.reportValidity()) event.preventDefault();
        // Standard POST /cart/add: submit the actual selected variant and pack
        // quantity. Shopify handles inventory, checkout totals and error pages.
      }, events);
      this.classList.add('is-enhanced');
      this.sync(false);
    }

    disconnectedCallback() {
      clearTimeout(this._childrenTimer);
      this._abort?.abort();
      this._connected = false;
      this.classList.remove('is-enhanced');
      // Remove generated availability suffixes before a section reattachment.
      this.buttons?.forEach(button => {
        button.textContent = button.dataset.reconFlavor === 'sl' ? 'Slayberry' : 'Boujee Berry';
      });
    }

    sync(resetQuantity) {
      const option = this.selected;
      if (!option) return;
      const ready = option.dataset.state === 'ready' && !option.disabled && !!option.value;
      this.dataset.flavor = option.dataset.flavor;
      this.select.disabled = !ready;
      this.select.value = ready ? option.value : '';
      this.quantity.disabled = !ready;
      this.quantity.min = option.dataset.min || '1';
      this.quantity.step = option.dataset.step || '1';
      if (option.dataset.max) this.quantity.max = option.dataset.max;
      else this.quantity.removeAttribute('max');
      if (resetQuantity) this.quantity.value = this.quantity.min;
      this.normalizeQuantity();
      this.price.textContent = ['ready', 'soldout'].includes(option.dataset.state) ? option.dataset.price : '';
      this.buy.disabled = !ready;
      this.buy.textContent = ready ? 'Add to cart' : option.dataset.state === 'soldout' ? 'Sold out' : 'Temporarily unavailable';
      this.status.textContent = ready ? '' : option.dataset.state === 'soldout'
        ? 'This flavor is currently sold out.' : 'This Recon Pack is temporarily unavailable.';
      this.buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.reconFlavor === this.dataset.flavor)));
      this.querySelectorAll('[data-recon-images]').forEach(group => {
        group.hidden = group.dataset.reconImages !== this.dataset.flavor;
      });
      this.querySelector('[data-recon-caption]').textContent = this.dataset.flavor === 'sl' ? 'Slayberry' : 'Boujee Berry';
      this.syncQuantityButtons();
    }

    normalizeQuantity() {
      const min = Number(this.quantity.min) || 1;
      const step = Number(this.quantity.step) || 1;
      const max = this.quantity.max ? Number(this.quantity.max) : Number.MAX_SAFE_INTEGER;
      let value = Number(this.quantity.value);
      if (!Number.isFinite(value) || !this.quantity.value) value = min;
      value = min + Math.round((value - min) / step) * step;
      const upper = min + Math.floor((max - min) / step) * step;
      this.quantity.value = String(Math.max(min, Math.min(upper, value)));
    }

    adjust(direction) {
      if (this.quantity.disabled) return;
      this.normalizeQuantity();
      this.quantity.value = String(Number(this.quantity.value) + direction * (Number(this.quantity.step) || 1));
      this.normalizeQuantity();
      this.syncQuantityButtons();
    }

    syncQuantityButtons() {
      const value = Number(this.quantity.value);
      const min = Number(this.quantity.min) || 1;
      const step = Number(this.quantity.step) || 1;
      const max = this.quantity.max ? Number(this.quantity.max) : Number.MAX_SAFE_INTEGER;
      this.minus.disabled = this.quantity.disabled || value - step < min;
      this.plus.disabled = this.quantity.disabled || value + step > max;
    }
  }
  customElements.define('aa-recon-pack', AAReconPack);
})();
